import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, OnInit, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormControlStatus, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { Button } from 'primeng/button';
import { NotificationService } from '../../../core/services';
import { PermissionService } from '../../../core/security';
import { UserService } from '../../user';
import { ReportWorkspaceService } from '../report-workspace.service';
import {
    CheckboxParameterComponent,
    CustomerAverageAnnualDeliveredUnitsParameterComponent,
    DatePeriodParameterComponent,
    DatePickerParameterComponent,
    DeliveryTypeMultiSelectParameterComponent,
    HierarchyTreeSelectParameter,
    InventoryTransactionTypeMultiSelectParameterComponent,
    MultiSelectParameterComponent,
    ProductCategoryCostSelectParameterComponent,
    ProductCategoryReportMultiSelectParameterComponent,
    RadioButtonParameterComponent,
    SelectParameterComponent,
    SubscriberSelectParameterComponent,
    TradeClassMultiSelectParameterComponent
} from './parameters';
import { ReportApiUrlDialogComponent } from './report-api-url-dialog/report-api-url-dialog.component';
import { ReportParameterDefinition } from './report-parameter.model';

@Component({
    selector: 'app-report-parameters',
    imports: [
        Button,
        CommonModule,
        ReactiveFormsModule,
        CheckboxParameterComponent,
        CustomerAverageAnnualDeliveredUnitsParameterComponent,
        DatePickerParameterComponent,
        DeliveryTypeMultiSelectParameterComponent,
        HierarchyTreeSelectParameter,
        InventoryTransactionTypeMultiSelectParameterComponent,
        MultiSelectParameterComponent,
        RadioButtonParameterComponent,
        SelectParameterComponent,
        SubscriberSelectParameterComponent,
        ProductCategoryCostSelectParameterComponent,
        ProductCategoryReportMultiSelectParameterComponent,
        TradeClassMultiSelectParameterComponent,
        DatePeriodParameterComponent,
        ReportApiUrlDialogComponent
    ],
    templateUrl: './report-parameters.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportParametersComponent implements OnInit, AfterViewInit {
    private destroyRef = inject(DestroyRef);
    private notificationService = inject(NotificationService);
    private permissionService = inject(PermissionService);
    private reportWorkspace = inject(ReportWorkspaceService);
    private userService = inject(UserService);

    parameterDefinitions = this.reportWorkspace.parameterDefinitions;

    parametersForm = new FormGroup({});

    /**
     * Indicates whether any parameter is currently performing an async operation (e.g., fetching options or dependent data).
     *
     * Used to disable actions such as changing the Subscriber while parameters are still resolving.
     */
    areAnyParametersLoading = this.reportWorkspace.areAnyParamsLoading;

    isReportRunning = this.reportWorkspace.isReportRunning;

    isSysAdmin = this.reportWorkspace.isSysAdmin;
    subscriberKey = this.reportWorkspace.subscriberKey;
    subscriber = this.reportWorkspace.subscriber;
    reportOptions = this.reportWorkspace.reportOptions;
    isSubscriberMissing = computed(() =>
        this.isSysAdmin() &&
        this.reportOptions().showSubscriberSelect &&
        this.subscriberKey() === null
    );

    /* Integration URL */
    showApiUrlButton = computed(() => {
        const def = this.reportWorkspace.reportDefinition();
        const hasIntegration = !!def?.integrationEndpoint && !!def?.buildIntegrationQueryParams;
        const hasApiKey = this.userService.userContext()?.hasApiKey === true;
        return hasIntegration && hasApiKey;
    });

    apiUrlButtonLoading = signal(false);
    integrationUrl = signal('');
    isApiUrlDialogVisible = signal(false);

    constructor() {
        /* Parameters Reset */
        effect(() => {
            const request = this.reportWorkspace.parametersResetRequest();
            if (!request) return;

            untracked(() => {
                const parameterValues = this.reportWorkspace.parameterValues();
                for (const def of this.parameterDefinitions()) {
                    const control = this.parametersForm.get(def.key);
                    if (control) control.reset(parameterValues[def.key]);
                }
            });
        });

        /* Auto Run */
        effect(() => {
            const autoRunRequest = this.reportWorkspace.autoRunRequest();
            const areParametersReady = this.reportWorkspace.areParametersReady();
            const areAnyParamsLoading = this.reportWorkspace.areAnyParamsLoading();
            const areParametersValid = this.reportWorkspace.areParametersValid();
            const isSubscriberMissing = this.isSubscriberMissing();

            /* No request to Auto Run */
            if (!autoRunRequest) {
                return;
            }

            /* Don't Auto Run if parameters are loading */
            if (areAnyParamsLoading) {
                return;
            }

            /* Don't Auto Run if a subscriber is required but not selected */
            if (isSubscriberMissing) {
                return;
            }

            /* Don't Auto Run if the Parameters component isn't ready or the parameters form is invalid */
            if (!areParametersReady || !areParametersValid ) {
                return;
            }

            this.submitForm(true);

            untracked(() => {
                this.reportWorkspace.clearAutoRunRequest();
            })
        });
    }

    ngOnInit() {
        const formGroup = this.createFormGroup();
        this.reportWorkspace.setParametersValid(formGroup.valid);

        formGroup.statusChanges
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((status: FormControlStatus) => {
                this.reportWorkspace.setParametersValid(status === 'VALID');
            });

        this.parametersForm = formGroup;
    }

    ngAfterViewInit() {
        this.reportWorkspace.setParametersReady(true);
    }

    /* Creates a new FormGroup using all Report ParameterDefs. */
    private createFormGroup(): FormGroup {
        const formGroup = new FormGroup({});

        for (const param of this.parameterDefinitions()) {
            const formControl = this.createFormControl(param);
            formGroup.addControl(param.key, formControl);
        }

        return formGroup;
    }

    /* Creates a FormControl for the specified Parameter Def. This is the general method used by all "standard" params */
    private createFormControl(def: ReportParameterDefinition) {
        let defaultValue = this.reportWorkspace.defaultParameterValues[def.key] ?? def.emptyValue ?? null;

        const isArray = Array.isArray(def.defaultValue);

        /* Apply validators only if the user has permission to see the parameter */
        let validators: ValidatorFn[] = [];
        if (!def.securityRule || this.permissionService.hasPermission(def.securityRule)) {
            validators = [...(def.required ? [Validators.required] : []), ...(def.validators ? def.validators : [])];
        }

        return new FormControl(defaultValue, {
            validators: validators,
            asyncValidators: def.asyncValidators ?? [],
            nonNullable: isArray
        });
    }

    submitForm(markTouched: boolean) {
        if (markTouched) {
            this.parametersForm.markAllAsTouched();
            this.reportWorkspace.setParametersFormSubmitted(true);
        }

        if (this.parametersForm.valid) {
            this.reportWorkspace.executeReport();
        }
    }

    cancelExecution() {
        this.reportWorkspace.cancelReport();
    }

    resetForm() {
        this.reportWorkspace.resetParameters();
    }

    canUseParameter(parameterDef: ReportParameterDefinition) {
        /* Don't Render based on Security */
        if (parameterDef.securityRule && !this.permissionService.hasPermission(parameterDef.securityRule)) {
            return false;
        }

        return true;
    }

    openApiUrlDialog() {
        this.parametersForm.markAllAsTouched();

        if (this.parametersForm.invalid) {
            return;
        }

        this.apiUrlButtonLoading.set(true);

        this.reportWorkspace
            .buildIntegrationUrl()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (url) => {
                    this.integrationUrl.set(url);
                    this.isApiUrlDialogVisible.set(true);
                    this.apiUrlButtonLoading.set(false);
                },
                error: (err) => {
                    this.apiUrlButtonLoading.set(false);
                    if (err.status === 404) {
                        this.notificationService.warn(
                            'No API Key',
                            "You don't have an API key. Contact your administrator."
                        );
                    } else {
                        this.notificationService.error('Error', 'Failed to retrieve your API key.');
                    }
                }
            });
    }
}
